import { api, type DocumentMeta, type FileBucket } from '../services/api';
import { resourcesApi, type Resource, type ExtractionType, type QualityTier } from '../services/resources-api';
import { FilterBar, type FilterBarConfig, type FilterState } from './FilterBar';
import { UNIFIED_FILTERS, UNIFIED_ACTIONS } from '../config/filterbar-config';
import { IngestModal } from './IngestModal';

// Extended DocumentMeta with Stage 2 fields
interface ExtendedDocMeta extends DocumentMeta {
  parentId?: string | null;
  extractionType?: ExtractionType | null;
  qualityTier?: QualityTier | null;
  childCount?: number;
}

// Map Resource to DocumentMeta for compatibility
function resourceToDocMeta(r: Resource): ExtendedDocMeta {
  return {
    id: r.id,
    title: r.title,
    tags: r.tags || [],
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    version: 1,
    source: (r.source as 'local' | 'gdrive' | 'import') || 'local',
    isKnowledgeEnabled: r.isKnowledgeEnabled,
    resourceCategory: r.resourceCategory,
    customFields: r.customFields,
    humanReview: r.humanReview,
    completionScore: r.completionScore,
    project: r.project || undefined,
    content: r.content || undefined,
    // Stage 2 fields
    parentId: r.parentId,
    extractionType: r.extractionType,
    qualityTier: r.qualityTier,
  };
}

export type ResourceSelectCallback = (doc: DocumentMeta, fromReviewMode?: boolean) => void;
export type ResourceCreateCallback = () => void;
export type FileUploadCallback = (bucket: FileBucket, files: File[]) => void;

export class KnowledgeBaseView {
  private documents: ExtendedDocMeta[] = [];
  private allTags: string[] = [];
  private filterBar: FilterBar | null = null;
  private currentFilterState: FilterState | null = null;
  private reviewModeActive: boolean = false;

  // Stage 2: Hierarchical view state
  private expandedParents: Set<string> = new Set();
  private childrenCache: Map<string, ExtendedDocMeta[]> = new Map();
  private parentChildCounts: Map<string, number> = new Map();

  private onResourceSelect: ResourceSelectCallback | null = null;
  private onResourceCreate: ResourceCreateCallback | null = null;

  // DOM elements
  private listEl: HTMLElement;
  private loadingEl: HTMLElement;
  private emptyEl: HTMLElement;
  private dropzoneEl: HTMLElement | null = null;
  private fileInputEl: HTMLInputElement | null = null;

  // File upload state
  private selectedBucket: FileBucket = 'resources';
  private isUploading = false;

  // Selected document for actions
  private selectedDoc: DocumentMeta | null = null;

  // Multi-select for bulk operations
  private selectedItems: Set<string> = new Set();

  // Preview panel
  private previewSection: HTMLElement | null = null;
  private previewTitle: HTMLElement | null = null;
  private previewMeta: HTMLElement | null = null;
  private previewContent: HTMLElement | null = null;
  private splitContainer: HTMLElement | null = null;
  private focusedIndex: number = -1;
  private itemElements: HTMLElement[] = [];
  private previewedDocId: string | null = null;
  private isEditingInfo: boolean = false;
  private currentPreviewDoc: DocumentMeta | null = null;
  private editingVisibleFields: Set<string> = new Set();
  private onPreviewChangeCallback?: () => void;

  constructor() {
    this.listEl = document.getElementById('kb-list')!;
    this.loadingEl = document.getElementById('kb-loading')!;
    this.emptyEl = document.getElementById('kb-empty')!;
    this.dropzoneEl = document.getElementById('kb-dropzone');
    this.fileInputEl = document.getElementById('kb-file-input') as HTMLInputElement;

    // Preview panel elements
    this.previewSection = document.getElementById('kb-preview-section');
    this.previewTitle = document.getElementById('kb-preview-title');
    this.previewMeta = document.getElementById('kb-preview-meta');
    this.previewContent = document.getElementById('kb-preview-content');
    this.splitContainer = document.querySelector('.kb-split-container');

    this.initFilterBar();
    this.initDropzone();
    this.setupPreviewHandlers();
    this.setupKeyboardNavigation();
  }

  private initDropzone(): void {
    if (!this.dropzoneEl || !this.fileInputEl) return;

    const dropzoneInner = this.dropzoneEl.querySelector('.kb-dropzone-inner');

    // Click to browse
    dropzoneInner?.addEventListener('click', () => {
      this.fileInputEl?.click();
    });

    // File input change
    this.fileInputEl.addEventListener('change', () => {
      const files = this.fileInputEl?.files;
      if (files && files.length > 0) {
        this.handleFileUpload(Array.from(files));
      }
    });

    // Drag and drop events
    this.dropzoneEl.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropzoneEl?.classList.add('drag-over');
    });

    this.dropzoneEl.addEventListener('dragleave', () => {
      this.dropzoneEl?.classList.remove('drag-over');
    });

    this.dropzoneEl.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzoneEl?.classList.remove('drag-over');

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        this.handleFileUpload(Array.from(files));
      }
    });

    // Bucket selection
    this.dropzoneEl.querySelectorAll('.kb-bucket-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const bucket = (btn as HTMLElement).dataset.bucket as FileBucket;
        this.selectBucket(bucket);
      });
    });
  }

  /**
   * Get currently previewed document (for AI operations)
   */
  public getCurrentPreviewDoc(): DocumentMeta | null {
    return this.currentPreviewDoc;
  }

  /**
   * Register callback for when preview state changes
   */
  public onPreviewChange(callback: () => void): void {
    this.onPreviewChangeCallback = callback;
  }

  private selectBucket(bucket: FileBucket): void {
    this.selectedBucket = bucket;

    // Update UI
    this.dropzoneEl?.querySelectorAll('.kb-bucket-btn').forEach(btn => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.bucket === bucket);
    });
  }

  private async handleFileUpload(files: File[]): Promise<void> {
    if (this.isUploading || files.length === 0) return;

    this.isUploading = true;
    const dropzoneInner = this.dropzoneEl?.querySelector('.kb-dropzone-inner');

    // Show progress UI
    let progressHtml = `
      <div class="kb-upload-progress">
        <div class="kb-upload-progress-bar">
          <div class="kb-upload-progress-fill" style="width: 0%"></div>
        </div>
        <div class="kb-upload-status">Uploading 0/${files.length} files...</div>
      </div>
    `;
    const progressContainer = document.createElement('div');
    progressContainer.innerHTML = progressHtml;
    dropzoneInner?.appendChild(progressContainer.firstElementChild!); 

    const progressFill = this.dropzoneEl?.querySelector('.kb-upload-progress-fill') as HTMLElement;
    const statusEl = this.dropzoneEl?.querySelector('.kb-upload-status');

    let uploaded = 0;
    let errors = 0;

    for (const file of files) {
      try {
        if (statusEl) {
          statusEl.textContent = `Uploading ${uploaded + 1}/${files.length}: ${file.name}`;
        }

        await api.uploadKnowledgeFile(file, this.selectedBucket);
        uploaded++;

        if (progressFill) {
          progressFill.style.width = `${(uploaded / files.length) * 100}%`;
        }
      } catch (error) {
        console.error('Upload error:', error);
        errors++;
      }
    }

    // Show result
    if (statusEl) {
      if (errors === 0) {
        statusEl.textContent = `Uploaded ${uploaded} files successfully!`;
      } else {
        statusEl.textContent = `Uploaded ${uploaded} files, ${errors} failed`;
      }
    }

    // Clear progress after delay
    setTimeout(() => {
      this.dropzoneEl?.querySelector('.kb-upload-progress')?.remove();
      this.isUploading = false;
      if (this.fileInputEl) this.fileInputEl.value = '';
    }, 2000);
  }

  private showNotification(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'kb-notification';
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: var(--bg-panel);
      border: 1px solid var(--border);
      color: var(--text-main);
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 13px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 3000;
      animation: slideIn 0.3s ease;
    `;
    notification.textContent = message;

    // Add animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    notification.appendChild(style);

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideIn 0.3s ease reverse';
      setTimeout(() => notification.remove(), 300);
    }, 4000);
  }

  private async _toggleBookmark(): Promise<void> {
    if (!this.selectedDoc) return;

    try {
      const newFavorite = !this.selectedDoc.isFavorite;
      await api.updateDocument(this.selectedDoc.id, { isFavorite: newFavorite });

      // Update local state
      this.selectedDoc.isFavorite = newFavorite;
      const docIndex = this.documents.findIndex(d => d.id === this.selectedDoc!.id);
      if (docIndex >= 0) {
        this.documents[docIndex].isFavorite = newFavorite;
      }

      // Update UI
      this.render();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  private _toggleReviewMode(): void {
    // Toggle review mode flag
    this.reviewModeActive = !this.reviewModeActive;

    // Update button visual state
    const reviewBtn = document.querySelector('[data-action="review"]');
    if (this.reviewModeActive) {
      reviewBtn?.classList.add('active');
    } else {
      reviewBtn?.classList.remove('active');
    }

    // Re-render with filter applied
    this.render();

    // Show notification
    const message = this.reviewModeActive
      ? 'Showing documents pending review'
      : 'Showing all documents';
    this.showNotification(message);
  }

  private readonly CACHE_KEY = 'kb_documents_cache';
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  async load(): Promise<void> {
    // 1. Try to show cached data immediately (stale-while-revalidate)
    const cached = this.loadFromCache();
    if (cached) {
      this.documents = cached;
      this.updateTagsFromDocuments();
      this.render();
    } else {
      this.showLoading();
    }

    // 2. Fetch fresh data in background
    this.fetchFreshData(!cached); // Show loading only if no cache
  }

  private loadFromCache(): DocumentMeta[] | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      // Return cached data (will be refreshed in background anyway)
      return data;
    } catch {
      return null;
    }
  }

  private saveToCache(documents: DocumentMeta[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify({
        data: documents,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Failed to cache documents:', e);
    }
  }

  private async fetchFreshData(showLoading: boolean = true): Promise<void> {
    if (showLoading) {
      this.showLoading();
    }

    try {
      // Use unified resources API - fetch only top-level items (no parentId)
      const response = await resourcesApi.list({ limit: 500, parentId: null });
      if (response.success && response.resources) {
        // Map to ExtendedDocMeta and filter by knowledge enabled
        const freshDocs = response.resources
          .map(resourceToDocMeta)
          .filter((doc: ExtendedDocMeta) => doc.isKnowledgeEnabled === true);

        // Check if data changed
        const hasChanged = JSON.stringify(freshDocs.map(d => d.id).sort()) !==
                          JSON.stringify(this.documents.map(d => d.id).sort());

        this.documents = freshDocs;
        this.saveToCache(freshDocs);
        this.updateTagsFromDocuments();

        // Fetch child counts for parents (Stage 2)
        await this.fetchChildCounts(freshDocs);

        // Only re-render if data changed or was loading
        if (hasChanged || showLoading) {
          this.render();
        }
      }
    } catch (error) {
      console.error('Error loading resources:', error);
      // If we had cached data, keep showing it
      if (this.documents.length === 0) {
        this.render(); // Show empty/error state
      }
    }
  }

  // Stage 2: Fetch child counts for all documents
  private async fetchChildCounts(docs: ExtendedDocMeta[]): Promise<void> {
    this.parentChildCounts.clear();

    // Batch fetch child counts (we could optimize this with a single API call)
    const promises = docs.map(async (doc) => {
      try {
        const result = await resourcesApi.countChildren(doc.id);
        if (result.success && result.count > 0) {
          this.parentChildCounts.set(doc.id, result.count);
          doc.childCount = result.count;
        }
      } catch (e) {
        // Ignore errors for individual counts
      }
    });

    // Execute in parallel with concurrency limit
    await Promise.all(promises);
  }

  private updateTagsFromDocuments(): void {
    const tagsSet = new Set<string>();
    this.documents.forEach(doc => {
      doc.tags?.forEach(tag => tagsSet.add(tag));
    });
    this.allTags = Array.from(tagsSet).sort();

    if (this.filterBar) {
      this.filterBar.setFilterOptions('tags', this.allTags.map(t => ({ value: t, label: t })));
    }
  }

  // Manual refresh - force fetch from server
  async refresh(): Promise<void> {
    this.showLoading();
    await this.fetchFreshData(true);
    this.showNotification('Refreshed');
  }

  // Filter by category (called from sidebar)
  async filterByCategory(category: string): Promise<void> {
    // Load documents if not loaded
    if (this.documents.length === 0) {
      await this.load();
    }

    // Clear filterBar UI first
    if (this.filterBar) {
      this.filterBar.clearAll();
    }

    // Initialize or reset filter state (clear ALL filters like setIntentFilter does)
    if (!this.currentFilterState) {
      this.currentFilterState = {
        search: '',
        activeFilters: new Map(),
        sortField: 'updated_at',
        sortDirection: 'desc'
      };
    } else {
      // Clear all existing filters
      this.currentFilterState.activeFilters.forEach((set) => set.clear());
      this.currentFilterState.search = '';
    }

    // Set only category filter
    this.currentFilterState!.activeFilters.set('category', new Set([category]));

    // Update filter bar UI
    if (this.filterBar) {
      this.filterBar.setActiveFilter('category', [category]);
    }

    this.render();
  }

  private showLoading(): void {
    this.loadingEl.classList.remove('hidden');
    this.emptyEl.classList.add('hidden');
    this.listEl.innerHTML = '';
  }

  private getFilteredDocuments(): DocumentMeta[] {
    let filtered = [...this.documents];
    const state = this.currentFilterState;

    if (!state) return filtered;

    // Filter by platform
    const platforms = state.activeFilters.get('platform');
    if (platforms && platforms.size > 0) {
      filtered = filtered.filter(doc => {
        const platform = (doc.customFields as Record<string, string>)?.platform;
        return platform && platforms.has(platform);
      });
    }

    // Filter by category
    const categories = state.activeFilters.get('category');
    if (categories && categories.size > 0) {
      filtered = filtered.filter(doc => {
        return doc.resourceCategory && categories.has(doc.resourceCategory);
      });
    }

    // Filter by tags
    const tags = state.activeFilters.get('tags');
    if (tags && tags.size > 0) {
      filtered = filtered.filter(doc => {
        return doc.tags?.some(tag => tags.has(tag));
      });
    }

    // Filter by intent (read_later, implement_later, etc.)
    const intents = state.activeFilters.get('intent');
    if (intents && intents.size > 0) {
      filtered = filtered.filter(doc => {
        const intent = (doc.customFields as Record<string, string>)?.intent;
        return intent && intents.has(intent);
      });
    }

    // Filter by views
    const viewsFilter = state.activeFilters.get('views');
    if (viewsFilter && viewsFilter.size > 0) {
      const viewsValue = Array.from(viewsFilter)[0]; // Radio button, only one value
      if (viewsValue && viewsValue !== 'any') {
        const threshold = this.parseEngagementThreshold(viewsValue);
        filtered = filtered.filter(doc => {
          const views = (doc.customFields as Record<string, number>)?.views as number | undefined;
          return views && views >= threshold;
        });
      }
    }

    // Filter by likes
    const likesFilter = state.activeFilters.get('likes');
    if (likesFilter && likesFilter.size > 0) {
      const likesValue = Array.from(likesFilter)[0]; // Radio button, only one value
      if (likesValue && likesValue !== 'any') {
        const threshold = this.parseEngagementThreshold(likesValue);
        filtered = filtered.filter(doc => {
          const likes = (doc.customFields as Record<string, number>)?.likes as number | undefined;
          return likes && likes >= threshold;
        });
      }
    }

    // Filter by search
    if (state.search) {
      const query = state.search.toLowerCase();
      filtered = filtered.filter(doc => {
        const title = doc.title?.toLowerCase() || '';
        const summary = ((doc.customFields as Record<string, string>)?.summary || '').toLowerCase();
        const docTags = doc.tags?.join(' ').toLowerCase() || '';
        return title.includes(query) || summary.includes(query) || docTags.includes(query);
      });
    }

    // Filter by review mode (show only red/orange items, score < 80)
    if (this.reviewModeActive) {
      filtered = filtered.filter(doc => {
        // Show only items that need work (score < 80)
        // Red: 0-49, Orange: 50-79
        return (doc.completionScore || 0) < 80;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (state.sortField === 'title') {
        aVal = a.title?.toLowerCase() || '';
        bVal = b.title?.toLowerCase() || '';
      } else if (state.sortField === 'views') {
        aVal = (a.customFields as Record<string, number>)?.views as number || 0;
        bVal = (b.customFields as Record<string, number>)?.views as number || 0;
      } else if (state.sortField === 'likes') {
        aVal = (a.customFields as Record<string, number>)?.likes as number || 0;
        bVal = (b.customFields as Record<string, number>)?.likes as number || 0;
      } else if (state.sortField === 'duration') {
        aVal = (a.customFields as Record<string, number>)?.duration as number || 0;
        bVal = (b.customFields as Record<string, number>)?.duration as number || 0;
      } else {
        aVal = a[state.sortField as 'updated_at' | 'created_at'] || 0;
        bVal = b[state.sortField as 'updated_at' | 'created_at'] || 0;
      }

      if (state.sortDirection === 'desc') {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      } else {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
    });

    return filtered;
  }

  private render(): void {
    this.loadingEl.classList.add('hidden');

    const filtered = this.getFilteredDocuments();

    if (filtered.length === 0) {
      this.emptyEl.classList.remove('hidden');
      this.listEl.innerHTML = '';
      // Close preview panel when no results
      this.closePreview();
      return;
    }

    this.emptyEl.classList.add('hidden');

    // Render with hierarchical structure (parents + expanded children)
    let html = '';
    for (const doc of filtered) {
      html += this.renderItem(doc as ExtendedDocMeta);

      // If parent is expanded, render children
      const childCount = this.parentChildCounts.get(doc.id) || 0;
      if (childCount > 0 && this.expandedParents.has(doc.id)) {
        const children = this.childrenCache.get(doc.id) || [];
        for (const child of children) {
          html += this.renderChildItem(child);
        }
      }
    }
    this.listEl.innerHTML = html;

    // Track item elements for keyboard navigation
    this.itemElements = Array.from(this.listEl.querySelectorAll('.kb-item')) as HTMLElement[];
    this.focusedIndex = -1;

    // Add click handlers
    this.listEl.querySelectorAll('.kb-item').forEach((item, index) => {
      const docId = (item as HTMLElement).dataset.id;

      item.addEventListener('click', (e) => {
        const doc = this.documents.find(d => d.id === docId);
        if (doc) {
          // Set as selected
          this.selectedDoc = doc;
          this.focusedIndex = index;

          // Show preview on single click
          this.showPreview(doc);

          // Double-click to open in editor
          if ((e as MouseEvent).detail === 2 && this.onResourceSelect) {
            this.onResourceSelect(doc);
          }
        }
      });

      // Hover action handlers
      item.querySelector('.kb-item-action-open')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const doc = this.documents.find(d => d.id === docId);
        if (doc && this.onResourceSelect) {
          this.onResourceSelect(doc);
        }
      });

      item.querySelector('.kb-item-action-delete')?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const doc = this.documents.find(d => d.id === docId) as ExtendedDocMeta | undefined;
        if (!doc) return;

        const childCount = this.parentChildCounts.get(doc.id) || 0;

        // Stage 2: If parent has children, show choice dialog
        if (childCount > 0) {
          await this.showDeleteWithChildrenDialog(doc, childCount);
        } else if (confirm(`Delete "${doc.title}"?`)) {
          await resourcesApi.delete(doc.id);
          this.documents = this.documents.filter(d => d.id !== docId);
          item.remove();
          this.showNotification(`Deleted "${doc.title}"`);
        }
      });

      // Stage 2: Expand/collapse handler
      item.querySelector('.kb-item-expand')?.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (!docId) return;
        await this.toggleExpand(docId);
      });

      // Checkbox handler
      item.querySelector('.kb-item-checkbox')?.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!docId) return;
        const checkbox = e.target as HTMLInputElement;
        if (checkbox.checked) {
          this.selectedItems.add(docId);
        } else {
          this.selectedItems.delete(docId);
        }
        this.updateSelectionBar();
      });
    });

    // Tag click handlers
    this.listEl.querySelectorAll('.kb-item-tag').forEach(tagEl => {
      tagEl.addEventListener('click', (e) => {
        e.stopPropagation();
        const tag = (tagEl as HTMLElement).dataset.tag!;
        // Add tag to filters and re-render
        if (this.currentFilterState) {
          this.currentFilterState.activeFilters.get('tags')?.add(tag);
          this.render();
        }
      });
    });

    // Category badge click handlers - show dropdown to change category
    this.listEl.querySelectorAll('.kb-item-badge').forEach(badgeEl => {
      badgeEl.addEventListener('click', (e) => {
        e.stopPropagation();
        const item = (badgeEl as HTMLElement).closest('.kb-item');
        const docId = item?.getAttribute('data-id');
        const doc = this.documents.find(d => d.id === docId);
        if (doc) {
          this.showCategoryDropdown(badgeEl as HTMLElement, doc);
        }
      });
    });

    // Intent quick-edit button handlers
    this.listEl.querySelectorAll('.kb-item-action-intent').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const docId = (btn as HTMLElement).dataset.docId;
        const doc = this.documents.find(d => d.id === docId);
        if (doc) {
          this.showIntentDropdown(btn as HTMLElement, doc);
        }
      });
    });

    // Intent badge click handler (to change intent)
    this.listEl.querySelectorAll('.kb-item-intent-badge').forEach(badge => {
      badge.addEventListener('click', (e) => {
        e.stopPropagation();
        const item = (badge as HTMLElement).closest('.kb-item');
        const docId = item?.getAttribute('data-id');
        const doc = this.documents.find(d => d.id === docId);
        if (doc) {
          this.showIntentDropdown(badge as HTMLElement, doc);
        }
      });
    });
  }

  private renderItem(doc: ExtendedDocMeta): string {
    const customFields = doc.customFields as Record<string, string | number> | undefined;
    const summary = customFields?.summary as string || '';
    const category = doc.resourceCategory || '';
    const intent = customFields?.intent as string || '';
    const tags = doc.tags || [];
    const dateStr = this.formatDate(doc.updated_at);
    const url = customFields?.url as string || '';

    // Virality metrics
    const views = customFields?.views as number | undefined;
    const likes = customFields?.likes as number | undefined;
    const duration = customFields?.duration as number | undefined;

    // Stage 2: Child count and expand state
    const childCount = this.parentChildCounts.get(doc.id) || 0;
    const isExpanded = this.expandedParents.has(doc.id);

    const badge = this.getBadgeForCategory(category);
    const intentBadge = this.getIntentBadge(intent);
    const transcriptionBadge = this.getTranscriptionBadge(doc);
    const viralityBadges = this.getViralityBadges(views, likes, duration);
    const scoreDot = this.getScoreDot(doc);
    const isChecked = this.selectedItems.has(doc.id);

    // External link icon if URL exists
    const externalLinkIcon = url ? `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="kb-item-external-link" title="Open link" onclick="event.stopPropagation();">
      <span class="material-symbols-outlined">open_in_new</span>
    </a>` : '';

    // Stage 2: Expand button for parents with children
    const expandBtn = childCount > 0 ? `
      <button class="kb-item-expand" title="${isExpanded ? 'Collapse' : 'Expand'} ${childCount} extracted elements">
        <span class="material-symbols-outlined">${isExpanded ? 'expand_less' : 'expand_more'}</span>
        <span class="kb-item-child-count">${childCount}</span>
      </button>
    ` : '';

    return `
      <div class="kb-item" data-id="${doc.id}">
        <div class="kb-item-content">
          <div class="kb-item-header">
            ${expandBtn}${externalLinkIcon}<h3 class="kb-item-title">${this.escapeHtml(doc.title || 'Untitled')}</h3>
          </div>
          <div class="kb-item-badges">
            <input type="checkbox" class="kb-item-checkbox" data-doc-id="${doc.id}" ${isChecked ? 'checked' : ''} />
            ${scoreDot}
            ${badge}
            ${intentBadge}
            ${transcriptionBadge}
          </div>
          ${summary ? `<p class="kb-item-excerpt">${this.escapeHtml(summary.slice(0, 150))}${summary.length > 150 ? '...' : ''}</p>` : ''}
          ${viralityBadges}
          <div class="kb-item-meta">
            <button class="kb-item-action kb-item-action-delete danger" title="Delete" style="background:none;border:none;padding:0;margin-right:0;cursor:pointer;">
              <span class="material-symbols-outlined" style="font-size:16px;color:var(--text-secondary);">delete</span>
            </button>
            ${tags.length > 0 ? `
              <div class="kb-item-tags">
                ${tags.slice(0, 3).map(t => `<span class="kb-item-tag" data-tag="${t}">#${t}</span>`).join('')}
                ${tags.length > 3 ? `<span class="kb-item-tags-more">+${tags.length - 3}</span>` : ''}
              </div>
            ` : ''}
            <span class="kb-item-date">${dateStr}</span>
          </div>
        </div>
      </div>
    `;
  }

  // Stage 2: Render child item (extracted element)
  private renderChildItem(doc: ExtendedDocMeta): string {
    const dateStr = this.formatDate(doc.updated_at);
    const extractionBadge = this.getExtractionTypeBadge(doc.extractionType);
    const qualityBadge = this.getQualityTierBadge(doc.qualityTier);
    const isChecked = this.selectedItems.has(doc.id);

    return `
      <div class="kb-item kb-item-child" data-id="${doc.id}" data-parent-id="${doc.parentId}">
        <div class="kb-item-content">
          <div class="kb-item-header">
            <span class="kb-item-indent"></span>
            <h3 class="kb-item-title">${this.escapeHtml(doc.title || 'Untitled')}</h3>
          </div>
          <div class="kb-item-badges">
            <input type="checkbox" class="kb-item-checkbox" data-doc-id="${doc.id}" ${isChecked ? 'checked' : ''} />
            ${extractionBadge}
            ${qualityBadge}
          </div>
          <div class="kb-item-meta">
            <button class="kb-item-action kb-item-action-delete danger" title="Delete" style="background:none;border:none;padding:0;margin-right:0;cursor:pointer;">
              <span class="material-symbols-outlined" style="font-size:16px;color:var(--text-secondary);">delete</span>
            </button>
            <span class="kb-item-date">${dateStr}</span>
          </div>
        </div>
      </div>
    `;
  }

  // Stage 2: Extraction type badge
  private getExtractionTypeBadge(type: ExtractionType | null | undefined): string {
    if (!type) return '';

    const typeInfo: Record<ExtractionType, { label: string; icon: string; color: string }> = {
      hook: { label: 'Hook', icon: 'priority_high', color: 'red' },
      angle: { label: 'Angle', icon: 'view_in_ar', color: 'purple' },
      loop: { label: 'Loop', icon: 'all_inclusive', color: 'blue' },
      stake: { label: 'Stake', icon: 'warning', color: 'orange' },
      structure: { label: 'Structure', icon: 'account_tree', color: 'green' },
      framework: { label: 'Framework', icon: 'hub', color: 'cyan' }
    };

    const info = typeInfo[type];
    if (!info) return '';

    return `<span class="kb-extraction-badge kb-extraction-${info.color}" title="${info.label}">
      <span class="material-symbols-outlined">${info.icon}</span>
      ${info.label}
    </span>`;
  }

  // Stage 2: Quality tier badge
  private getQualityTierBadge(tier: QualityTier | null | undefined): string {
    if (!tier || tier === 'standard') return '';

    if (tier === 'premium') {
      return `<span class="kb-quality-badge premium" title="Premium quality">
        <span class="material-symbols-outlined">star</span>
      </span>`;
    }

    return '';
  }

  // Stage 2: Toggle expand/collapse
  private async toggleExpand(parentId: string): Promise<void> {
    if (this.expandedParents.has(parentId)) {
      // Collapse
      this.expandedParents.delete(parentId);
      this.render();
    } else {
      // Expand - load children if not cached
      if (!this.childrenCache.has(parentId)) {
        const result = await resourcesApi.getChildren(parentId);
        if (result.success && result.resources) {
          const children = result.resources.map(resourceToDocMeta);
          this.childrenCache.set(parentId, children);
        }
      }
      this.expandedParents.add(parentId);
      this.render();
    }
  }

  // Stage 2: Delete with children dialog
  private async showDeleteWithChildrenDialog(doc: ExtendedDocMeta, childCount: number): Promise<void> {
    const dialog = document.createElement('div');
    dialog.className = 'kb-delete-dialog-overlay';
    dialog.innerHTML = `
      <div class="kb-delete-dialog">
        <h3>Delete "${this.escapeHtml(doc.title)}"?</h3>
        <p>This document has <strong>${childCount}</strong> extracted element${childCount > 1 ? 's' : ''}.</p>
        <div class="kb-delete-dialog-options">
          <button class="kb-delete-btn delete-all" data-action="delete-all">
            <span class="material-symbols-outlined">delete_forever</span>
            Delete All
            <span class="kb-delete-btn-desc">Remove document and all extracted elements</span>
          </button>
          <button class="kb-delete-btn keep-children" data-action="keep-children">
            <span class="material-symbols-outlined">content_cut</span>
            Keep Elements
            <span class="kb-delete-btn-desc">Delete document, keep extracted elements as standalone</span>
          </button>
        </div>
        <button class="kb-delete-cancel">Cancel</button>
      </div>
    `;

    document.body.appendChild(dialog);

    return new Promise((resolve) => {
      dialog.querySelector('.kb-delete-cancel')?.addEventListener('click', () => {
        dialog.remove();
        resolve();
      });

      dialog.querySelector('[data-action="delete-all"]')?.addEventListener('click', async () => {
        dialog.remove();
        const result = await resourcesApi.deleteWithChildren(doc.id, true);
        if (result.success) {
          this.documents = this.documents.filter(d => d.id !== doc.id);
          this.childrenCache.delete(doc.id);
          this.parentChildCounts.delete(doc.id);
          this.showNotification(`Deleted "${doc.title}" and ${result.data.deleted - 1} elements`);
          this.render();
        }
        resolve();
      });

      dialog.querySelector('[data-action="keep-children"]')?.addEventListener('click', async () => {
        dialog.remove();
        const result = await resourcesApi.deleteWithChildren(doc.id, false);
        if (result.success) {
          this.documents = this.documents.filter(d => d.id !== doc.id);
          this.childrenCache.delete(doc.id);
          this.parentChildCounts.delete(doc.id);
          this.showNotification(`Deleted "${doc.title}", kept ${result.data.orphaned} elements`);
          await this.fetchFreshData(false); // Refresh to show orphaned elements
        }
        resolve();
      });

      // Close on overlay click
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) {
          dialog.remove();
          resolve();
        }
      });
    });
  }

  private getIntentBadge(intent: string): string {
    if (!intent) return '';

    const intentLabels: Record<string, { label: string; color: string }> = {
      'read_later': { label: 'Read Later', color: 'yellow' },
      'implement_later': { label: 'Apply Later', color: 'orange' },
      'reference': { label: 'Reference', color: 'blue' },
      'archived': { label: 'Archived', color: 'gray' }
    };

    const info = intentLabels[intent];
    if (!info) return '';

    return `<span class="kb-item-intent-badge kb-intent-${info.color}" data-intent="${intent}">${info.label}</span>`;
  }

  private getTranscriptionBadge(doc: DocumentMeta): string {
    const customFields = doc.customFields as Record<string, string> | undefined;
    const url = customFields?.url || '';
    const transcription = customFields?.transcription || '';
    const transcriptionStatus = customFields?.transcriptionStatus || '';

    // Determine status
    let status = 'disabled';
    let icon = 'closed_caption';

    if (transcriptionStatus === 'failed') {
      status = 'failed';
    } else if (transcriptionStatus === 'processing') {
      status = 'processing';
    } else if (transcription) {
      status = 'completed';
    } else if (this.isVideoUrl(url)) {
      status = 'available';
    }

    // Don't show badge for disabled state
    if (status === 'disabled') return '';

    return `<span class="kb-transcription-badge ${status}">
      <span class="material-symbols-outlined">${icon}</span>
    </span>`;
  }

  // Small transcription icon for meta row (before tags)
  private getTranscriptionIcon(doc: DocumentMeta): string {
    const customFields = doc.customFields as Record<string, string> | undefined;
    const url = customFields?.url || '';
    const transcription = customFields?.transcription || '';
    const transcriptionStatus = customFields?.transcriptionStatus || '';

    // Determine status
    let status = 'disabled';
    if (transcriptionStatus === 'failed') {
      status = 'failed';
    } else if (transcriptionStatus === 'processing') {
      status = 'processing';
    } else if (transcription) {
      status = 'completed';
    } else if (this.isVideoUrl(url)) {
      status = 'available';
    }

    // Don't show icon for disabled state
    if (status === 'disabled') return '';

    return `<span class="kb-transcription-icon ${status}" title="Transcription: ${status}">
      <span class="material-symbols-outlined">closed_caption</span>
    </span>`;
  }

  private getViralityBadges(views?: number, likes?: number, duration?: number): string {
    const badges: string[] = [];

    if (views) {
      badges.push(`<span class="kb-virality-badge views">
        <span class="material-symbols-outlined">visibility</span>
        ${this.formatNumber(views)}
      </span>`);
    }

    if (likes) {
      badges.push(`<span class="kb-virality-badge likes">
        <span class="material-symbols-outlined">favorite</span>
        ${this.formatNumber(likes)}
      </span>`);
    }

    if (duration) {
      badges.push(`<span class="kb-virality-badge duration">
        <span class="material-symbols-outlined">schedule</span>
        ${this.formatDuration(duration)}
      </span>`);
    }

    if (badges.length === 0) return '';

    return `<div class="kb-item-virality">${badges.join('')}</div>`;
  }

  private getScoreDot(doc: DocumentMeta): string {
    if (doc.completionScore === undefined) return '';

    const score = Math.max(0, Math.min(100, doc.completionScore));
    let color = '#d1d5db'; // gray (default/low)

    if (score >= 80) {
      color = '#10b981'; // green
    } else if (score >= 50) {
      color = '#f59e0b'; // yellow/orange
    } else {
      color = '#ef4444'; // red
    }

    return `<span class="kb-score-dot" style="background: ${color}" title="Completion: ${score}%"></span>`;
  }

  private isVideoUrl(url: string): boolean {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('youtube.com') ||
           lower.includes('youtu.be') ||
           lower.includes('tiktok.com') ||
           lower.includes('instagram.com/reel') ||
           lower.includes('instagram.com/p/') ||
           lower.endsWith('.mp4') ||
           lower.endsWith('.mov') ||
           lower.endsWith('.avi');
  }

  private getIconForResource(doc: DocumentMeta): string {
    const customFields = doc.customFields as Record<string, string> | undefined;
    const platform = customFields?.platform?.toLowerCase() || '';
    const url = customFields?.url || '';
    const title = doc.title?.toLowerCase() || '';

    if (platform.includes('youtube') || url.includes('youtube')) return 'smart_display';
    if (platform.includes('instagram')) return 'photo_camera';
    if (platform.includes('tiktok')) return 'music_video';
    if (platform.includes('twitter') || platform.includes('x')) return 'alternate_email';
    if (platform.includes('linkedin')) return 'work';
    if (platform.includes('podcast')) return 'podcasts';
    if (platform.includes('blog')) return 'article';
    if (title.endsWith('.pdf')) return 'picture_as_pdf';
    if (title.includes('book')) return 'book';
    return 'description';
  }

  private getBadgeForCategory(category: string): string {
    if (!category) return '';

    const colorMap: Record<string, string> = {
      'Content Creation': 'blue',
      'Business': 'green',
      'Marketing': 'purple',
      'Paid Ads': 'orange',
      'Programming': 'cyan',
      'Video Editing': 'red',
      'AI Prompts': 'pink',
      'Personal Brand': 'indigo'
    };

    const color = colorMap[category] || 'gray';
    return `<span class="kb-item-badge kb-badge-${color}" data-category="${category}">${category}</span>`;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Number formatting utilities
  private formatNumber(num: number | undefined): string {
    if (!num || num === 0) return '';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  }

  private formatDuration(seconds: number | undefined): string {
    if (!seconds || seconds === 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private parseEngagementThreshold(value: string): number {
    const multipliers: Record<string, number> = {
      '1k': 1000,
      '10k': 10000,
      '100k': 100000,
      '1m': 1000000,
      '10m': 10000000
    };
    return multipliers[value] || 0;
  }

  // Callbacks
  onResourceSelectCallback(callback: ResourceSelectCallback): void {
    this.onResourceSelect = callback;
  }

  onResourceCreateCallback(callback: ResourceCreateCallback): void {
    this.onResourceCreate = callback;
  }

  // ═══════════════════════════════════════════════════════════════
  // PREVIEW PANEL
  // ═══════════════════════════════════════════════════════════════

  private setupPreviewHandlers(): void {
    // Close button
    document.getElementById('kb-preview-close')?.addEventListener('click', () => {
      this.closePreview();
    });

    // Edit button - open in editor
    document.getElementById('kb-preview-edit')?.addEventListener('click', () => {
      if (this.previewedDocId && this.onResourceSelect) {
        const doc = this.documents.find(d => d.id === this.previewedDocId);
        if (doc) this.onResourceSelect(doc, this.reviewModeActive);
      }
    });

    // ESC to close preview
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.previewSection && !this.previewSection.classList.contains('hidden')) {
        this.closePreview();
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (this.previewSection && !this.previewSection.classList.contains('hidden')) {
        // Up/Down arrows to navigate list
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.navigateList(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.navigateList(-1);
        } else if (e.key === 'Enter') {
          // Open editor
          e.preventDefault();
          if (this.currentPreviewDoc && this.onResourceSelect) {
            this.onResourceSelect(this.currentPreviewDoc, this.reviewModeActive);
          }
        }
      }
    });
  }

  private setupKeyboardNavigation(): void {
    // Basic keyboard navigation for the list when preview is closed
    // TODO: Implement more robust focus management
  }

  private navigateList(direction: number): void {
    if (this.itemElements.length === 0) return;

    let newIndex = this.focusedIndex + direction;

    // Clamp index
    if (newIndex < 0) newIndex = 0;
    if (newIndex >= this.itemElements.length) newIndex = this.itemElements.length - 1;

    if (newIndex !== this.focusedIndex) {
      this.focusedIndex = newIndex;
      const item = this.itemElements[this.focusedIndex];
      const docId = item.dataset.id;

      if (docId) {
        const doc = this.documents.find(d => d.id === docId);
        if (doc) {
          // Scroll into view
          item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

          // Highlight selection
          this.itemElements.forEach(el => el.classList.remove('selected'));
          item.classList.add('selected');

          // Update preview
          this.showPreview(doc);
        }
      }
    }
  }

  private showPreview(doc: DocumentMeta): void {
    if (!this.previewSection || !this.previewTitle || !this.previewMeta || !this.previewContent) return;

    this.currentPreviewDoc = doc;
    this.previewedDocId = doc.id;
    this.editingVisibleFields.clear(); // Reset visible fields override

    // Update URL with resource ID
    const url = new URL(window.location.href);
    url.searchParams.set('resource', doc.id);
    history.pushState({ resourceId: doc.id, view: 'knowledge' }, '', url.toString());

    // Show preview panel
    this.previewSection.classList.remove('hidden');
    this.splitContainer?.classList.add('preview-open');

    // Notify listeners (App.ts uses this to show AI buttons)
    if (this.onPreviewChangeCallback) {
      this.onPreviewChangeCallback();
    }

    // Populate content
    this.previewTitle.textContent = doc.title || 'Untitled';

    // Render meta fields (read-only view)
    this.renderPreviewMeta(doc);

    // Fetch and render content
    this.previewContent.innerHTML = '<div class="kb-preview-loading">Loading content...</div>';

    // Fetch content
    resourcesApi.get(doc.id).then(response => {
      if (response.success && response.data) {
        // If it's a knowledge item, render nicely. If it's a note, render markdown.
        // For now, simple text display
        const content = response.data.content || '';
        // Simple markdown rendering (or just text)
        this.previewContent!.innerHTML = this.renderPreviewContent(content);
      } else {
        this.previewContent!.innerHTML = '<div class="kb-preview-error">Failed to load content</div>';
      }
    });
  }

  // BUG 20+21 FIX: Categories list for dropdown
  private readonly CATEGORIES = [
    'Content Creation', 'Business', 'Marketing', 'Paid Ads', 'Programming',
    'Video Editing', 'AI Prompts', 'Personal Brand', 'Uncategorized'
  ];

  private renderPreviewMeta(doc: DocumentMeta): void {
    if (!this.previewMeta) return;

    const custom = (doc.customFields as Record<string, any>) || {};
    const metadata = (doc as any).metadata || {};
    const score = doc.completionScore ?? 0;
    const humanReview = doc.humanReview;
    const reviewLevel = metadata.reviewLevel || (humanReview ? 'ok' : null);

    // BUG 20 FIX: Use editable select/input instead of div
    let html = `
      <div class="kb-preview-meta-grid">
        <div class="kb-meta-item">
          <label>Category</label>
          <select class="kb-meta-select" data-field="resourceCategory" data-doc-id="${doc.id}">
            ${this.CATEGORIES.map(cat =>
              `<option value="${cat}" ${cat === (doc.resourceCategory || 'Uncategorized') ? 'selected' : ''}>${cat}</option>`
            ).join('')}
          </select>
        </div>
        <div class="kb-meta-item">
          <label>Score</label>
          <div class="kb-meta-value kb-score-badge" style="color: ${this.getScoreColor(score)}">${score}%</div>
        </div>
        <div class="kb-meta-item">
          <label>Status</label>
          <div class="kb-meta-value">${humanReview ? '<span class="kb-reviewed-badge">Reviewed</span>' : 'Pending'}</div>
        </div>
        <div class="kb-meta-item">
          <label>Project</label>
          <input type="text" class="kb-meta-input" data-field="project" data-doc-id="${doc.id}"
                 value="${doc.project || ''}" placeholder="No project">
        </div>
        <div class="kb-meta-item full-width">
          <label>Tags</label>
          <input type="text" class="kb-meta-input" data-field="tags" data-doc-id="${doc.id}"
                 value="${(doc.tags || []).join(', ')}" placeholder="tag1, tag2, tag3">
        </div>
    `;

    // Add custom fields (read-only)
    if (custom.url) {
      html += `
        <div class="kb-meta-item full-width">
          <label>URL</label>
          <div class="kb-meta-value"><a href="${custom.url}" target="_blank">${custom.url}</a></div>
        </div>
      `;
    }

    if (custom.summary) {
      html += `
        <div class="kb-meta-item full-width">
          <label>Summary</label>
          <div class="kb-meta-value">${custom.summary}</div>
        </div>
      `;
    }

    html += `</div>`; // Close grid

    // Review buttons
    html += `
      <div class="kb-review-actions">
        <span class="kb-review-label">Review:</span>
        <button class="kb-review-btn trash ${reviewLevel === 'trash' ? 'active' : ''}" data-level="trash" title="Trash - Delete">
          <span class="material-symbols-outlined">delete</span>
        </button>
        <button class="kb-review-btn wrong ${reviewLevel === 'wrong' ? 'active' : ''}" data-level="wrong" title="Wrong - Bad classification">
          <span class="material-symbols-outlined">close</span>
        </button>
        <button class="kb-review-btn ok ${reviewLevel === 'ok' ? 'active' : ''}" data-level="ok" title="OK - Use for training">
          <span class="material-symbols-outlined">check</span>
        </button>
        <button class="kb-review-btn gold ${reviewLevel === 'gold' ? 'active' : ''}" data-level="gold" title="Gold - Perfect example">
          <span class="material-symbols-outlined">star</span>
        </button>
      </div>
    `;

    this.previewMeta.innerHTML = html;

    // BUG 21 FIX: Add handlers to save changes
    this.bindMetaEditors(doc.id);

    // Add click handlers for review buttons
    this.previewMeta.querySelectorAll('.kb-review-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const level = (btn as HTMLElement).dataset.level as 'trash' | 'wrong' | 'ok' | 'gold';
        await this.handleReview(doc.id, level);
      });
    });
  }

  // BUG 21 FIX: Bind change handlers to editable fields
  private bindMetaEditors(docId: string): void {
    if (!this.previewMeta) return;

    // Selects - immediate save on change
    this.previewMeta.querySelectorAll('.kb-meta-select').forEach(select => {
      select.addEventListener('change', async (e) => {
        const field = (e.target as HTMLSelectElement).dataset.field!;
        const value = (e.target as HTMLSelectElement).value;
        await this.updateField(docId, field, value);
      });
    });

    // Inputs - debounced save
    this.previewMeta.querySelectorAll('.kb-meta-input').forEach(input => {
      let timeout: number;
      input.addEventListener('input', (e) => {
        clearTimeout(timeout);
        timeout = window.setTimeout(async () => {
          const field = (e.target as HTMLInputElement).dataset.field!;
          let value: string | string[] = (e.target as HTMLInputElement).value;

          // Parse tags as array
          if (field === 'tags') {
            value = value.split(',').map((t: string) => t.trim()).filter(Boolean);
          }

          await this.updateField(docId, field, value);
        }, 500); // 500ms debounce
      });
    });
  }

  // BUG 21 FIX: Save field changes to API
  private async updateField(docId: string, field: string, value: string | string[]): Promise<void> {
    try {
      const result = await resourcesApi.update(docId, {
        [field]: value,
        humanReview: true, // Mark as human reviewed when user edits
      });

      if (result.success) {
        this.showNotification('Saved');
        // Update local document
        const doc = this.documents.find(d => d.id === docId);
        if (doc && result.data) {
          (doc as any)[field] = value;
          doc.humanReview = true;
          if (result.data.completionScore !== undefined) {
            doc.completionScore = result.data.completionScore;
          }
          this.render(); // Update list view
        }
      } else {
        this.showNotification('Failed to save');
      }
    } catch (error) {
      console.error('Failed to update field:', error);
      this.showNotification('Error saving');
    }
  }

  private async handleReview(docId: string, level: 'trash' | 'wrong' | 'ok' | 'gold'): Promise<void> {
    // Confirm trash
    if (level === 'trash') {
      if (!confirm('Delete this item permanently?')) return;
    }

    try {
      const result = await resourcesApi.review(docId, level);

      if (result.success) {
        // Show notification
        const messages: Record<string, string> = {
          trash: 'Item deleted',
          wrong: 'Marked as wrong classification',
          ok: 'Approved for training',
          gold: 'Marked as gold example'
        };
        this.showNotification(messages[level] + (result.retrainTriggered ? ' (retrain triggered)' : ''));

        // If deleted, close preview and refresh
        if (level === 'trash') {
          this.closePreview();
          this.documents = this.documents.filter(d => d.id !== docId);
          this.render();
        } else {
          // Update local doc and re-render preview
          const doc = this.documents.find(d => d.id === docId);
          if (doc && result.data) {
            doc.humanReview = result.data.humanReview;
            doc.completionScore = result.data.completionScore;
            (doc as any).metadata = result.data.metadata;
            this.renderPreviewMeta(doc);
            this.render(); // Update list item score dot
          }
        }
      } else {
        this.showNotification('Failed to update review');
      }
    } catch (error) {
      console.error('Review error:', error);
      this.showNotification('Error updating review');
    }
  }

  private getScoreColor(score: number): string {
    if (score >= 80) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  }

  private renderPreviewContent(content: string): string {
    if (!content) return '<div class="kb-preview-empty">No content</div>';

    // Basic formatting
    return content
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/\*\*(.*)\*\*/gim, '<b>$1</b>')
      .replace(/\*(.*)\*/gim, '<i>$1</i>')
      .replace(/\n/gim, '<br>');
  }

  private closePreview(): void {
    if (this.previewSection) {
      this.previewSection.classList.add('hidden');
    }
    this.splitContainer?.classList.remove('preview-open');
    this.currentPreviewDoc = null;
    this.previewedDocId = null;

    // Notify listeners
    if (this.onPreviewChangeCallback) {
      this.onPreviewChangeCallback();
    }
  }

  // Dropdowns for quick editing
  private showCategoryDropdown(target: HTMLElement, doc: DocumentMeta): void {
    // Create dropdown
    const dropdown = document.createElement('div');
    dropdown.className = 'kb-dropdown-menu';
    
    const categories = [
      'Content Creation', 'Business', 'Marketing', 'Paid Ads', 
      'Programming', 'Video Editing', 'AI Prompts', 'Personal Brand'
    ];

    categories.forEach(cat => {
      const item = document.createElement('div');
      item.className = 'kb-dropdown-item';
      item.textContent = cat;
      item.onclick = async () => {
        await resourcesApi.update(doc.id, { resourceCategory: cat });
        // Update local state
        doc.resourceCategory = cat;
        this.render();
        dropdown.remove();
      };
      dropdown.appendChild(item);
    });

    // Position and show
    document.body.appendChild(dropdown);
    const rect = target.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom + 5}px`;
    dropdown.style.left = `${rect.left}px`;

    // Close on click outside
    const closeHandler = (e: MouseEvent) => {
      if (!dropdown.contains(e.target as Node) && e.target !== target) {
        dropdown.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  private showIntentDropdown(target: HTMLElement, doc: DocumentMeta): void {
    const dropdown = document.createElement('div');
    dropdown.className = 'kb-dropdown-menu';
    
    const intents = [
      { id: 'read_later', label: 'Read Later' },
      { id: 'implement_later', label: 'Apply Later' },
      { id: 'reference', label: 'Reference' },
      { id: 'archived', label: 'Archived' }
    ];

    intents.forEach(intent => {
      const item = document.createElement('div');
      item.className = 'kb-dropdown-item';
      item.textContent = intent.label;
      item.onclick = async () => {
        await resourcesApi.update(doc.id, {
          customFields: { ...doc.customFields, intent: intent.id }
        });
        // Update local state
        if (!doc.customFields) doc.customFields = {};
        doc.customFields.intent = intent.id;
        this.render();
        dropdown.remove();
      };
      dropdown.appendChild(item);
    });

    document.body.appendChild(dropdown);
    const rect = target.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom + 5}px`;
    dropdown.style.left = `${rect.left}px`;

    const closeHandler = (e: MouseEvent) => {
      if (!dropdown.contains(e.target as Node) && e.target !== target) {
        dropdown.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  // Bulk actions
  private updateSelectionBar(): void {
    let bar = document.getElementById('kb-selection-bar');
    
    if (this.selectedItems.size > 0) {
      if (!bar) {
        bar = document.createElement('div');
        bar.id = 'kb-selection-bar';
        bar.className = 'kb-selection-bar';
        bar.innerHTML = `
          <div class="kb-selection-count">${this.selectedItems.size} selected</div>
          <div class="kb-selection-actions">
            <button data-action="delete" class="danger">Delete</button>
            <button data-action="archive">Archive</button>
            <button data-action="clear">Clear</button>
          </div>
        `;
        this.listEl.parentElement?.appendChild(bar);

        // Add handlers
        bar.querySelector('[data-action="clear"]')?.addEventListener('click', () => {
          this.selectedItems.clear();
          this.render();
          this.updateSelectionBar();
        });

        bar.querySelector('[data-action="delete"]')?.addEventListener('click', async () => {
          if (confirm(`Delete ${this.selectedItems.size} items?`)) {
            const result = await resourcesApi.bulk(Array.from(this.selectedItems), 'delete');
            if (result.success) {
              this.showNotification(`Deleted ${result.data.succeeded} items`);
            }
            this.selectedItems.clear();
            await this.fetchFreshData(true);
          }
        });
      } else {
        bar.querySelector('.kb-selection-count')!.textContent = `${this.selectedItems.size} selected`;
      }
    } else {
      bar?.remove();
    }
  }

  // Helpers for filter bar
  private initFilterBar(): void {
    // Initial configuration for filter bar
    const config: FilterBarConfig = {
      showSearch: true,
      showApplyButton: true,
      filters: UNIFIED_FILTERS,
      showSort: true,
      customActions: UNIFIED_ACTIONS,
      viewContext: 'knowledge',
      title: 'Knowledge Base',
      titleBadge: { count: 0, label: 'items' }
    };

    this.filterBar = new FilterBar('knowledge-filter-bar', config);

    // Handle apply
    this.filterBar.onApplyCallback((state) => {
      this.currentFilterState = state;
      this.render();
    });

    // Handle search (live filtering)
    this.filterBar.onFilterChangeCallback((state) => {
      this.currentFilterState = state;
    });

    // Handle action buttons
    this.filterBar.onActionCallback((actionId) => {
      this.handleAction(actionId);
    });
  }

  private handleAction(actionId: string): void {
    switch (actionId) {
      case 'refresh':
        this.refresh();
        break;
      case 'add':
        if (this.onResourceCreate) {
          this.onResourceCreate();
        }
        break;
      case 'review':
        this._toggleReviewMode();
        break;
      case 'upload':
        this.fileInputEl?.click();
        break;
      case 'export':
        this.handleExport();
        break;
    }
  }

  private async handleExport(): Promise<void> {
    const format = confirm('Export as CSV? (Cancel for JSON)') ? 'csv' : 'json';

    try {
      this.showNotification('Exporting...');
      const result = await resourcesApi.exportData(format);

      if (result instanceof Blob) {
        // CSV blob
        const url = URL.createObjectURL(result);
        const a = document.createElement('a');
        a.href = url;
        a.download = `resources.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // JSON response
        const blob = new Blob([JSON.stringify(result.resources, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'resources.json';
        a.click();
        URL.revokeObjectURL(url);
      }

      this.showNotification(`Exported ${format.toUpperCase()}`);
    } catch (error) {
      this.showNotification('Export failed');
    }
  }

  // Quick filters called from Sidebar
  public setIntentFilter(intent: string | null): void {
    // Clear filterBar UI first
    if (this.filterBar) {
      this.filterBar.clearAll();
    }

    // Initialize or reset filter state
    if (!this.currentFilterState) {
      this.currentFilterState = {
        search: '',
        activeFilters: new Map(),
        sortField: 'updated_at',
        sortDirection: 'desc'
      };
    } else {
      // Clear all existing filters
      this.currentFilterState.activeFilters.forEach((set) => set.clear());
      this.currentFilterState.search = '';
    }

    // Set intent filter (only if intent is provided)
    if (intent) {
      this.currentFilterState!.activeFilters.set('intent', new Set([intent]));
    }

    // Update filter bar UI to reflect change
    if (this.filterBar && intent) {
      this.filterBar.setActiveFilter('intent', [intent]);
    }

    this.render();
  }

  // Open a resource by ID (for external navigation, e.g., from Inbox)
  public async openResource(id: string): Promise<void> {
    // First check if we already have it loaded
    let doc = this.documents.find(d => d.id === id);

    // If not loaded, fetch from API
    if (!doc) {
      const response = await resourcesApi.get(id);
      if (response.success && response.data) {
        doc = resourceToDocMeta(response.data);
      }
    }

    if (doc) {
      this.showPreview(doc);
    }
  }

  // Get currently selected/previewed document ID
  public getSelectedDocId(): string | undefined {
    return this.previewedDocId || undefined;
  }
}